import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ArrearAccept,
  type ArrearCalculation,
  type ArrearPreviewRequest,
  type PayRunCreate,
  type PayRunDisburse,
  type PayRunDetail,
  type PayRunInputsResult,
  type PayRunInputsUpsert,
  type PayRunLinesQuery,
  type PayRunLinesResponse,
  type PayRunListQuery,
  type PayRunListResponse,
  type PayRunLineAdjust,
  type PayRunSpecialCreate,
  type SettlementCandidatesResponse,
  type SpecialRunPreview,
  arrearCalculationSchema,
  payRunDetailSchema,
  payRunInputsResultSchema,
  payRunLinesResponseSchema,
  payRunListResponseSchema,
  settlementCandidatesResponseSchema,
  specialRunPreviewSchema,
  type PayRunSubmit,
} from '@thrivehrm/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

type ListParams = Partial<PayRunListQuery> & Pick<PayRunListQuery, 'page' | 'pageSize'>;
type LineParams = Partial<PayRunLinesQuery> & Pick<PayRunLinesQuery, 'page' | 'pageSize'>;

/** `GET /payroll/runs` (AC-8: payroll.read, or payroll.disburse for approved runs). */
export function usePayRuns(params: ListParams) {
  return useQuery({
    queryKey: queryKeys.payroll.runList(params),
    queryFn: ({ signal }) =>
      api.get<PayRunListResponse>('/payroll/runs', {
        query: { ...params },
        schema: payRunListResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** `GET /payroll/runs/:id`. */
export function usePayRun(id: string) {
  return useQuery({
    queryKey: queryKeys.payroll.run(id),
    queryFn: ({ signal }) =>
      api.get<PayRunDetail>(`/payroll/runs/${id}`, { schema: payRunDetailSchema, signal }),
  });
}

/** `GET /payroll/runs/:id/lines`. */
export function usePayRunLines(id: string, params: LineParams) {
  return useQuery({
    queryKey: queryKeys.payroll.runLines(id, params),
    queryFn: ({ signal }) =>
      api.get<PayRunLinesResponse>(`/payroll/runs/${id}/lines`, {
        query: { ...params },
        schema: payRunLinesResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** `GET /payroll/settlements/candidates` — leavers who can be settled (FR-PAY-14). */
export function useSettlementCandidates() {
  return useQuery({
    queryKey: queryKeys.payroll.settlementCandidates,
    queryFn: ({ signal }) =>
      api.get<SettlementCandidatesResponse>('/payroll/settlements/candidates', {
        schema: settlementCandidatesResponseSchema,
        signal,
      }),
  });
}

/** `POST /payroll/runs` — creates and calculates a monthly, bonus, LTA or settlement run. */
export function useCreatePayRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PayRunCreate) =>
      api.post<PayRunDetail>('/payroll/runs', body, { schema: payRunDetailSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.payroll.runs }),
  });
}

/** `POST /payroll/runs/preview` — who a bonus / LTA run would pay (US-P6); nothing is created. */
export function usePreviewSpecialRun() {
  return useMutation({
    mutationFn: (body: PayRunSpecialCreate) =>
      api.post<SpecialRunPreview>('/payroll/runs/preview', body, {
        schema: specialRunPreviewSchema,
      }),
  });
}

/** `PUT /payroll/runs/:id/adjustments/:employeeId` — override or exclude a special-run line. */
export function useAdjustRunLine(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, ...body }: PayRunLineAdjust & { employeeId: string }) =>
      api.put<PayRunDetail>(`/payroll/runs/${id}/adjustments/${employeeId}`, body, {
        schema: payRunDetailSchema,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.payroll.runs }),
  });
}

/** `PUT /payroll/runs/:id/inputs` — the run is recalculated, so refetch it and its lines. */
export function usePutRunInputs(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PayRunInputsUpsert) =>
      api.put<PayRunInputsResult>(`/payroll/runs/${id}/inputs`, body, {
        schema: payRunInputsResultSchema,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.payroll.runs }),
  });
}

/** `POST /payroll/runs/:id/recalculate` or `/cancel`. */
export function useRunAction(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: 'recalculate' | 'cancel') =>
      api.post<PayRunDetail>(`/payroll/runs/${id}/${action}`, {}, { schema: payRunDetailSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.payroll.runs }),
  });
}

/**
 * `POST /payroll/runs/:id/submit` (FR-PAY-11, FR-PAY-23): `confirmExcludeZeroLines` leaves the
 * zero-salary lines out; without it the API answers 409 when there are any.
 */
export function useSubmitRun(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PayRunSubmit) =>
      api.post<PayRunDetail>(`/payroll/runs/${id}/submit`, body, { schema: payRunDetailSchema }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.payroll.runs }),
  });
}

/** `POST /payroll/runs/:id/arrears/preview` — the arrear calculation (FR-PAY-15). */
export function usePreviewArrear(id: string) {
  return useMutation({
    mutationFn: (body: ArrearPreviewRequest) =>
      api.post<ArrearCalculation>(`/payroll/runs/${id}/arrears/preview`, body, {
        schema: arrearCalculationSchema,
      }),
  });
}

/** `POST /payroll/runs/:id/arrears` — adds the arrear; the run is recalculated. */
export function useAcceptArrear(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ArrearAccept) =>
      api.post<PayRunDetail>(`/payroll/runs/${id}/arrears`, body, { schema: payRunDetailSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.payroll.runs }),
  });
}

/** `POST /payroll/runs/:id/disburse` — one part paid (FR-PAY-17). */
export function useDisburse(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PayRunDisburse) =>
      api.post<PayRunDetail>(`/payroll/runs/${id}/disburse`, body, { schema: payRunDetailSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.payroll.runs }),
  });
}
